import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/services/authAPI';

const steps = ['Enter Email/Phone', 'Enter OTP', 'Set New Password'];

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Form data for all steps
  const [formData, setFormData] = useState({
    identifier: '', // email or phone
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    setError(''); // Clear error when user types
  };

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.identifier) {
      setError('Please enter your email or phone number');
      return;
    }

    setLoading(true);
    
    try {
      const result = await authAPI.forgotPassword(formData.identifier);
      
      if (result.success) {
        setSuccess(result.message);
        setActiveStep(1); // Move to OTP step
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.otp || formData.otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setActiveStep(2); // Move to new password step
  };

  const handleStep3Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords (must match backend requirements)
    if (!formData.newPassword || formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (!/[A-Z]/.test(formData.newPassword)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(formData.newPassword)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!/\d/.test(formData.newPassword)) {
      setError('Password must contain at least one number');
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.newPassword)) {
      setError('Password must contain at least one special character');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    
    try {
      const result = await authAPI.resetPassword(
        formData.identifier,
        formData.otp,
        formData.newPassword
      );
      
      if (result.success) {
        setSuccess(result.message);
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Box component="form" onSubmit={handleStep1Submit}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your email address or phone number and we'll send you a code to reset your password.
      </Typography>

      <TextField
        fullWidth
        label="Email or Phone Number"
        type="text"
        value={formData.identifier}
        onChange={handleChange('identifier')}
        margin="normal"
        required
        placeholder="email@example.com or +1234567890"
        autoFocus
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ mt: 3, mb: 2 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Code'}
      </Button>
    </Box>
  );

  const renderStep2 = () => (
    <Box component="form" onSubmit={handleStep2Submit}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We've sent a 6-digit code to <strong>{formData.identifier}</strong>. 
        Enter the code below to continue.
      </Typography>

      <TextField
        fullWidth
        label="6-Digit OTP Code"
        type="text"
        value={formData.otp}
        onChange={handleChange('otp')}
        margin="normal"
        required
        placeholder="123456"
        inputProps={{ maxLength: 6 }}
        autoFocus
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ mt: 3, mb: 2 }}
      >
        Verify Code
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="text"
          onClick={() => setActiveStep(0)}
          color="primary"
        >
          Use Different Email/Phone
        </Button>
      </Box>
    </Box>
  );

  const renderStep3 = () => (
    <Box component="form" onSubmit={handleStep3Submit}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Create a new password for your account.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
        Must be at least 8 characters with uppercase, lowercase, number, and special character.
      </Typography>

      <TextField
        fullWidth
        label="New Password"
        type={showPassword ? 'text' : 'password'}
        value={formData.newPassword}
        onChange={handleChange('newPassword')}
        margin="normal"
        required
        autoComplete="new-password"
        autoFocus
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(prev => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                size="small"
              >
                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="Confirm New Password"
        type={showConfirmPassword ? 'text' : 'password'}
        value={formData.confirmPassword}
        onChange={handleChange('confirmPassword')}
        margin="normal"
        required
        autoComplete="new-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(prev => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                size="small"
              >
                {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ mt: 3, mb: 2 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
        Reset Password
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Step Content */}
      {activeStep === 0 && renderStep1()}
      {activeStep === 1 && renderStep2()}
      {activeStep === 2 && renderStep3()}

      {/* Back to Login */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Link 
          onClick={() => navigate('/auth/login')} 
          variant="body2" 
          sx={{ cursor: 'pointer' }}
        >
          Back to Sign In
        </Link>
      </Box>
    </Box>
  );
};